import type { PropType } from "vue";

import { reactive, toRef, useId } from "vue";

import type { UseDescriptionScoped } from "@/lib/components/logic/atoms/useDescription.ts";
import type { UseLabelScoped } from "@/lib/components/logic/atoms/useLabel.ts";
import type { UseSubtextScoped } from "@/lib/components/logic/atoms/useSubtext.ts";
import type { UseTooltipScoped } from "@/lib/components/logic/atoms/useTooltip.ts";
import type {
  UseValidationProviderEmits,
  UseValidationProviderProps,
} from "@/lib/validation/ValidationProvider/useValidationProvider.ts";

import * as useDescription from "@/lib/components/logic/atoms/useDescription.ts";
import * as useLabel from "@/lib/components/logic/atoms/useLabel.ts";
import * as useSubtext from "@/lib/components/logic/atoms/useSubtext.ts";
import * as useTooltip from "@/lib/components/logic/atoms/useTooltip.ts";
import {
  emitsDefinition,
  pickProps,
  propsDefinition,
} from "@/lib/composables/componentComposable.ts";
import { useAriaIds } from "@/lib/composables/useAriaIds.ts";
import { mergeReactive } from "@/lib/helpers/reactivity.ts";
import {
  useValidationProvider,
  useValidationProviderEmits,
  useValidationProviderScoped,
} from "@/lib/validation/ValidationProvider/useValidationProvider.ts";

const scoped = <ModelValue>() =>
  propsDefinition({
    ...useValidationProviderScoped<ModelValue>(),
    ...useLabel.scoped,
    ...useTooltip.scoped,
    ...useDescription.scoped,
    ...useSubtext.scoped,
    ariaDescribedby: { type: String, required: false },
    ariaLabelledby: { type: String, required: false },
    modelValue: {
      type: [String, Number, Object, Array, Boolean] as PropType<ModelValue>,
      required: false,
    },
  });

const props = <ModelValue>() =>
  propsDefinition({
    ...scoped<ModelValue>(),
    labelId: { type: String, required: false },
    fieldset: { type: Boolean, required: false },
  });

const emits = emitsDefinition(useValidationProviderEmits);

type UseBaseInputScoped<ModelValue> = UseDescriptionScoped &
  UseLabelScoped &
  UseSubtextScoped &
  UseTooltipScoped &
  UseValidationProviderProps<ModelValue> & {
    ariaDescribedby?: string;
    ariaLabelledby?: string;
    modelValue?: ModelValue;
  };

type UseBaseInputProps<ModelValue> = UseBaseInputScoped<ModelValue> & {
  fieldset?: boolean;
  labelId?: string;
};

type UseBaseInputEmit = UseValidationProviderEmits;

function use<ModelValue>(
  props: UseBaseInputProps<ModelValue>,
  emit: UseBaseInputEmit,
) {
  const id = useId();

  const describedBy = useAriaIds(
    {
      tooltip: () => props.tooltip,
      description: () => props.description,
      subtext: () => props.subtext,
    },
    () => props.ariaDescribedby,
  );

  const {
    validationListeners,
    error,
    errorComponent,
    errorProps,
    displayInvalid,
    errorId,
    inputProps,
  } = useValidationProvider(() => props.modelValue as ModelValue, props, emit);

  const labelAtom = {
    props: mergeReactive(pickProps(props, useLabel.scoped), {
      labelId: toRef(() => props.labelId),
      for: toRef(() => (!props.fieldset && !props.labelId ? id : undefined)),
    }),
  };

  const tooltipAtom = {
    props: mergeReactive(pickProps(props, useTooltip.scoped), {
      tooltipId: describedBy.ids.tooltip,
    }),
  };

  const descriptionAtom = {
    props: mergeReactive(pickProps(props, useDescription.scoped), {
      descriptionId: describedBy.ids.description,
    }),
  };

  const inputSlot = reactive({
    id,
    describedBy,
    displayInvalid,
    errorId,
    aria: {
      ["aria-describedby"]: describedBy.aria,
      ["aria-errormessage"]: errorId,
      ["aria-labelledby"]: toRef(() => props.ariaLabelledby),
    },
    validation: { props: inputProps, on: validationListeners },
  });

  const subtextAtom = {
    props: mergeReactive(pickProps(props, useSubtext.scoped), {
      subtextId: describedBy.ids.subtext,
    }),
  };

  return {
    labelAtom,
    tooltipAtom,
    descriptionAtom,
    subtextAtom,
    inputSlot,
    displayInvalid,
    errorId,
    error,
    errorComponent,
    errorProps,
  };
}

export { emits, props, scoped, use };
export type { UseBaseInputEmit, UseBaseInputProps, UseBaseInputScoped };
